import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import {
  AvatarFromSmplReqPayload,
  AvatarFromStarReqPayload,
  GarmentReqPayload,
  MessageType,
  addAvatarFromSmplListener,
  addAvatarFromStarListener,
  addGarmentFromSkuListener,
  addGarmentListener,
  addSessionListener,
  isEmbedded,
  sendMessage,
} from '../../iframe';
import { setAvatarFromSmpl, setAvatarFromStar } from '../actions/homeAvatarAnonymous';
import {
  setGarment,
  setGarmentRecommendation,
  setGarmentRecommendationFromSku,
} from '../actions/homeGarment';
import { setSession } from '../actions/homeSession';
import { HomeState } from '../store/homeState';
import { initHomeStateExt } from '../store/homeStateExt';

export const useInitHome = () => {
  const isAvatarBuilding = useSelector((state: HomeState) => state.isAvatarBuilding);
  const isFitBuilding = useSelector((state: HomeState) => state.isFitBuilding);
  const isHeatMap = useSelector((state: HomeState) => state.isHeatMap);
  const isSizeRecommendationWaiting = useSelector(
    (state: HomeState) => state.isSizeRecommendationWaiting,
  );

  const { dispatch, getState } = useStore<HomeState>();

  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  // Set avatar from SMPL.
  const avatarFromSmplHandler = useCallback(
    (payload: AvatarFromSmplReqPayload) => {
      setAvatarFromSmpl(dispatch, getState, payload);
    },
    [dispatch, getState],
  );

  // Set avatar from STAR.
  const avatarFromStarHandler = useCallback(
    (payload: AvatarFromStarReqPayload) => {
      setAvatarFromStar(dispatch, getState, payload);
    },
    [dispatch, getState],
  );

  // Set garment.
  const garmentHandler = useCallback(
    (payload: GarmentReqPayload) => {
      if (payload.isSizeRecommendation) {
        setGarmentRecommendation(dispatch, getState, payload.id);
      } else {
        setGarment(dispatch, getState, payload.id);
      }
    },
    [dispatch, getState],
  );

  // Set garment from SKU.
  const garmentFromSkuHandler = useCallback(
    (payload: string) => {
      setGarmentRecommendationFromSku(dispatch, getState, payload);
    },
    [dispatch, getState],
  );

  // Set session.
  const sessionHandler = useCallback(
    (payload: string) => {
      setSession(dispatch, getState, payload);
    },
    [dispatch, getState],
  );

  useEffect(() => {
    // Reset home state.
    initHomeStateExt();

    // Register message listeners.
    if (isEmbedded()) {
      addAvatarFromSmplListener(avatarFromSmplHandler);
      addAvatarFromStarListener(avatarFromStarHandler);
      addGarmentListener(garmentHandler);
      addGarmentFromSkuListener(garmentFromSkuHandler);
      addSessionListener(sessionHandler);
    }

    setIsLoaded(true);

    // Send app loaded event.
    if (isEmbedded()) {
      sendMessage({ type: MessageType.APP_LOADED_EVENT });
    }
  }, [
    avatarFromSmplHandler,
    avatarFromStarHandler,
    garmentHandler,
    garmentFromSkuHandler,
    sessionHandler,
  ]);

  const isWaiting = useMemo(() => {
    return isAvatarBuilding || isFitBuilding || isSizeRecommendationWaiting;
  }, [isAvatarBuilding, isFitBuilding, isSizeRecommendationWaiting]);

  return { isLoaded, isHeatMap, isWaiting };
};
